import React, { useState, useCallback } from 'react';
import { Stack, Text, Input, Button, ControlLink, Overlay, LoadingBalls } from '@moda/om';
import { formatPrice, useForm } from '@moda/portal-stanchions';
import { reportError } from '../../../lib/reportError';
import {
  useCart,
  useUpdateCartMutation,
  CartUpdatePropertyName,
  CartUpdateAction
} from '../../../hooks/useCart';
import { FormButtonsLayout } from '../../FormButtonsLayout';

import './CheckoutSiteCreditForm.scss';

enum Mode {
  Resting,
  Saving,
  Error
}

type Props = {
  onClose: () => void;
};

export const CheckoutSiteCreditForm: React.FC<Props> = ({ onClose }) => {
  const [mode, setMode] = useState(Mode.Resting);
  const {
    cart: {
      attributes: {
        siteCreditAvailable,
        paymentSummary: { siteCredit: siteCreditApplied, payToday }
      }
    }
  } = useCart();
  const [updateCart] = useUpdateCartMutation();
  const onSubmit = useCallback(
    async ({ amount }: { amount: string }) => {
      setMode(Mode.Saving);
      try {
        if (Number(siteCreditApplied.price) !== Number(amount)) {
          await updateCart(CartUpdateAction.SiteCredit, {
            [CartUpdatePropertyName.SiteCredit]: Number(amount),
            ...(Number(amount) !== 0 &&
            siteCreditAvailable &&
            Number(siteCreditAvailable.price) > Number(amount)
              ? { [CartUpdatePropertyName.SiteCreditAutoApply]: false }
              : {})
          });
        }
        onClose();
      } catch (error) {
        reportError('CHECKOUT_SITE_CREDIT_SUBMIT', error);
        setMode(Mode.Error);
      }
    },
    [siteCreditApplied.price, onClose, updateCart, siteCreditAvailable]
  );

  const onClear = useCallback(() => {
    onSubmit({ amount: '0' });
  }, [onSubmit]);

  const { errors, handleSubmit } = useForm({
    onSubmit,
    validations: {
      amount: {
        required: {
          message: 'Please enter an amount to apply.',
          value: true
        },
        maxAmount: {
          message: 'Exceeded maximum amount available, please try again.',
          value: siteCreditAvailable ? Number(siteCreditAvailable.price) : undefined
        }
      }
    }
  });

  if (!siteCreditAvailable) return null;

  return (
    <form className="CheckoutSiteCreditForm" onSubmit={handleSubmit} noValidate>
      <Stack space={3}>
        <Text treatment="eyebrow">Site Credit</Text>
        <Text>
          Total site credit available is{' '}
          {formatPrice(siteCreditAvailable.currency, siteCreditAvailable.price)}
        </Text>
        <Input
          name="amount"
          type="number"
          defaultValue={
            Number(siteCreditApplied.price) === 0
              ? Math.min(Number(siteCreditAvailable.price), Number(payToday.price))
              : Number(siteCreditApplied.price)
          }
          error={
            errors.amount?.message ||
            (mode === Mode.Error ? 'An error occurred. Please retry later.' : undefined)
          }
        />
        <FormButtonsLayout>
          <ControlLink
            data-test-id="checkout-site-credit--continue"
            type="button"
            onClick={onClear}
          >
            I do not want to apply site credit
          </ControlLink>
          <Button data-test-id="checkout-site-credit--apply" type="submit">
            Apply
          </Button>
        </FormButtonsLayout>
      </Stack>

      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </form>
  );
};
